import {tennisBall, tennisRacket} from '@lucide/lab';
import {
  categoryColors,
  isCategoryColor,
  isCategoryIcon,
} from 'common/categoryAppearance';
import {
  Baby,
  BadgeDollarSign,
  Banknote,
  Beer,
  Bike,
  BookOpen,
  Briefcase,
  Building,
  Bus,
  Camera,
  Car,
  CarTaxiFront,
  Cat,
  ChefHat,
  Code,
  Coffee,
  Coins,
  CreditCard,
  Dices,
  Dog,
  Donut,
  Droplets,
  Dumbbell,
  Film,
  Fuel,
  Gamepad2,
  Gift,
  GraduationCap,
  Hammer,
  HandCoins,
  HeartHandshake,
  HeartPulse,
  Home,
  Lamp,
  Landmark,
  Laptop,
  Martini,
  Music,
  Percent,
  Phone,
  Piano,
  PiggyBank,
  Pill,
  Pizza,
  Plane,
  Receipt,
  Repeat,
  Scissors,
  Shapes,
  Shirt,
  ShoppingBag,
  ShoppingCart,
  Sofa,
  Spade,
  Sparkles,
  Stethoscope,
  Tag,
  TrainFront,
  TramFront,
  Trees,
  TrendingUp,
  Tv,
  Umbrella,
  UserRound,
  UsersRound,
  Utensils,
  Wifi,
  Wrench,
  Zap,
  createLucideIcon,
} from 'lucide-react';
import type {CategoryColor, CategoryIcon} from 'common/categoryAppearance';
import type {LucideIcon} from 'lucide-react';

// Lucide Lab icons (experimental set, shipped as bare icon nodes rather
// than components) — wrapped here so they render like any other entry.
const TennisBall = createLucideIcon('tennis-ball', tennisBall);
const TennisRacket = createLucideIcon('tennis-racket', tennisRacket);

// Exhaustively typed against the canonical name list in
// common/categoryAppearance, so adding a name there without a component
// here is a type error.
export const categoryIconComponents: Record<CategoryIcon, LucideIcon> = {
  baby: Baby,
  'badge-dollar-sign': BadgeDollarSign,
  banknote: Banknote,
  beer: Beer,
  bike: Bike,
  'book-open': BookOpen,
  briefcase: Briefcase,
  building: Building,
  bus: Bus,
  camera: Camera,
  car: Car,
  'car-taxi-front': CarTaxiFront,
  cat: Cat,
  'chef-hat': ChefHat,
  code: Code,
  coffee: Coffee,
  coins: Coins,
  'credit-card': CreditCard,
  dices: Dices,
  dog: Dog,
  donut: Donut,
  droplets: Droplets,
  dumbbell: Dumbbell,
  film: Film,
  fuel: Fuel,
  'gamepad-2': Gamepad2,
  gift: Gift,
  'graduation-cap': GraduationCap,
  hammer: Hammer,
  'hand-coins': HandCoins,
  'heart-handshake': HeartHandshake,
  'heart-pulse': HeartPulse,
  home: Home,
  lamp: Lamp,
  landmark: Landmark,
  laptop: Laptop,
  martini: Martini,
  music: Music,
  percent: Percent,
  phone: Phone,
  piano: Piano,
  'piggy-bank': PiggyBank,
  pill: Pill,
  pizza: Pizza,
  plane: Plane,
  receipt: Receipt,
  repeat: Repeat,
  scissors: Scissors,
  shapes: Shapes,
  shirt: Shirt,
  'shopping-bag': ShoppingBag,
  'shopping-cart': ShoppingCart,
  sofa: Sofa,
  spade: Spade,
  sparkles: Sparkles,
  stethoscope: Stethoscope,
  tag: Tag,
  'tennis-ball': TennisBall,
  'tennis-racket': TennisRacket,
  'train-front': TrainFront,
  'tram-front': TramFront,
  trees: Trees,
  'trending-up': TrendingUp,
  tv: Tv,
  umbrella: Umbrella,
  'user-round': UserRound,
  'users-round': UsersRound,
  utensils: Utensils,
  wifi: Wifi,
  wrench: Wrench,
  zap: Zap,
};

// Tint pairs (background + light/dark text) per color name. Full class
// strings as literals so Tailwind includes them in the build.
export const categoryTintClasses: Record<CategoryColor, string> = {
  red: 'bg-red-500/15 text-red-700 dark:text-red-400',
  orange: 'bg-orange-500/15 text-orange-700 dark:text-orange-400',
  amber: 'bg-amber-500/15 text-amber-700 dark:text-amber-400',
  emerald: 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-400',
  teal: 'bg-teal-500/15 text-teal-700 dark:text-teal-400',
  sky: 'bg-sky-500/15 text-sky-700 dark:text-sky-400',
  violet: 'bg-violet-500/15 text-violet-700 dark:text-violet-400',
  pink: 'bg-pink-500/15 text-pink-700 dark:text-pink-400',
};

// Text-only tints for category icons rendered inline in list meta lines:
// no background circle, and dimmed to 80% so the icon reads as part of the
// muted line rather than a second badge competing with the avatar.
export const categoryInlineIconClasses: Record<CategoryColor, string> = {
  red: 'text-red-700/80 dark:text-red-400/80',
  orange: 'text-orange-700/80 dark:text-orange-400/80',
  amber: 'text-amber-700/80 dark:text-amber-400/80',
  emerald: 'text-emerald-700/80 dark:text-emerald-400/80',
  teal: 'text-teal-700/80 dark:text-teal-400/80',
  sky: 'text-sky-700/80 dark:text-sky-400/80',
  violet: 'text-violet-700/80 dark:text-violet-400/80',
  pink: 'text-pink-700/80 dark:text-pink-400/80',
};

// Solid dots for the color picker swatches.
export const categorySwatchClasses: Record<CategoryColor, string> = {
  red: 'bg-red-500',
  orange: 'bg-orange-500',
  amber: 'bg-amber-500',
  emerald: 'bg-emerald-500',
  teal: 'bg-teal-500',
  sky: 'bg-sky-500',
  violet: 'bg-violet-500',
  pink: 'bg-pink-500',
};

// Form-state normalizers: a stored name that has since been removed from
// the canonical list is treated as automatic (null), matching how
// CategoryAvatar renders it — otherwise edit forms would resend the
// obsolete value and the server would reject unrelated edits.
export const validCategoryIconOrNull = (
  value: string | null | undefined,
): CategoryIcon | null =>
  value != null && isCategoryIcon(value) ? value : null;

export const validCategoryColorOrNull = (
  value: string | null | undefined,
): CategoryColor | null =>
  value != null && isCategoryColor(value) ? value : null;

// Fallback when a category has no explicit color: a stable hash of the
// name picks one of the tints, so the same category always renders the
// same color without storing anything.
export const automaticCategoryColor = (name: string): CategoryColor => {
  let hash = 0;
  for (const char of name) {
    hash = (hash * 31 + char.charCodeAt(0)) | 0;
  }
  return categoryColors[Math.abs(hash) % categoryColors.length];
};
